import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { FriendProfileBasicInfoPanel } from 'src/game/friends/components/FriendProfileBasicInfoPanel';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { FriendProfilePetInfoPanel } from 'src/game/friends/components/FriendProfilePetInfoPanel';
import { UserProfileDetails } from 'src/replicant/friends/friends.asyncGetters';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { acceptFriendRequest, rejectFriendRequest } from '../friends.sequences';
import { AssetGroup } from '@play-co/timestep-core';
import { user } from 'src/StateObserver';
import { FriendshipRequest } from 'src/replicant/friends/friends.ruleset';
import { assertNever } from 'src/replicant/utils';
import { ImageView } from '@play-co/timestep-core/ui';
import { removeFriend, visitFriend } from '../friendsList.sequences';

const assets = {
  dashedLine: 'assets/ui/shared/frames/dashed_line.png',
};

export type UserProfileContext =
  | 'friend'
  | 'sent_request'
  | 'received_request'
  | 'add_friend';
export type UserProfileAction =
  | 'unfriend'
  | 'visit'
  | 'acceptRequest'
  | 'rejectRequest'
  | 'addFriend'
  | undefined;

type CloseHandler = (action: UserProfileAction) => void;

export default class PopupUserProfile extends PopupBasic {
  private content: FlexBoxView;
  private player: FriendProfileBasicInfoPanel;
  private pet: FriendProfilePetInfoPanel;
  private buttonRow: FlexBoxView;
  private visitButton?: ButtonScaleViewWithText;
  private acceptButton?: ButtonScaleViewWithText;
  private rejectButton?: ButtonScaleViewWithText;
  private closeHandler: CloseHandler;
  private currentContext: UserProfileContext = 'friend';
  private currentPlayerId?: string;
  private currentData?: UserProfileDetails;

  protected getAssetsGroup() {
    return new AssetGroup([
      ...Object.values(assets),
      ...FriendProfileBasicInfoPanel.assets,
      ...FriendProfilePetInfoPanel.assets,
    ]);
  }

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 600,
      darkerBg: false,
      close: () => opts.close(undefined),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE,
      },
    });

    this.closeHandler = opts.close;

    // Create content wrapper container
    this.content = new FlexBoxView({
      superview: this.box,
      flex: {
        width: this.box.style.width,
        height: this.box.style.height,
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 90,
      },
    });

    // Create player basic info panel
    this.player = new FriendProfileBasicInfoPanel(this.content);

    // Create pet info panel
    this.pet = new FriendProfilePetInfoPanel(this.content);

    const buttonWidth = 230;
    const buttonHeight = 90;
    const buttonMargin = 10;

    // Create dashed separator
    new ImageView({
      superview: this.content,
      image: assets.dashedLine,
      width: 389,
      height: 10,
    });

    // Create button row container
    this.buttonRow = new FlexBoxView({
      superview: this.content,
      flex: {
        width: this.box.style.width - 40,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 15,
      },
    });

    const baseButtonOpts = {
      superview: this.buttonRow,
      width: buttonWidth,
      height: buttonHeight,
      labelOffsetX: -5,
      labelOffsetY: -1,
      fontSize: 38,
      font: 'Body' as const,
      centerOnOrigin: false,
    };

    // Visit button (for friends)
    this.visitButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      ...baseButtonOpts,
      localeText: () => i18n('userProfile.visit'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.handleVisit();
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
    });

    // Reject button (for received requests)
    this.rejectButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      ...baseButtonOpts,
      localeText: () => i18n('userProfile.reject'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        await this.handleReject();
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_REJECT,
      },
    });

    // Accept button (for received requests)
    this.acceptButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      ...baseButtonOpts,
      localeText: () => i18n('userProfile.accept'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.handleAccept();
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_ACCEPT,
      },
    });

    // Set margins for inner items in button row
    for (const child of this.buttonRow.getSubviews()) {
      if (child) {
        getFlexNode(child).updateStyle({
          marginLeft: buttonMargin,
          marginRight: buttonMargin,
        });
      }
    }
  }

  public init(opts: {
    playerId: string;
    context: UserProfileContext;
    data: UserProfileDetails;
  }) {
    super.init({
      title: this.getTitleByContext(opts.context),
    });

    this.currentContext = opts.context;
    this.currentPlayerId = opts.playerId;
    this.currentData = opts.data;

    if (opts.context === 'add_friend') {
      // Wire panel's add-friend button to close popup with 'addFriend' action
      this.player.setAddFriendHandler(() => this.closeHandler('addFriend'));
    } else {
      // Set up unfriend callback (only relevant for friends)
      this.player.setUnfriendHandler(async () => {
        const success = await removeFriend(opts.playerId);
        if (success) this.closeHandler('unfriend');
      });
    }

    this.updateButtonVisibility();

    this.player.setData(opts.data);
    this.pet.setData(opts.data);
  }

  private updateButtonVisibility() {
    const isFriend = this.currentContext === 'friend';
    const isSentRequest = this.currentContext === 'sent_request';
    const isReceivedRequest = this.currentContext === 'received_request';
    const isAddFriend = this.currentContext === 'add_friend';

    this.visitButton?.updateOpts({
      visible: isFriend || isSentRequest || isAddFriend,
    });
    this.acceptButton?.updateOpts({ visible: isReceivedRequest });
    this.rejectButton?.updateOpts({ visible: isReceivedRequest });
  }

  private getTitleByContext(context: UserProfileContext) {
    switch (context) {
      case 'friend':
        return i18n('userProfile.statusFriend');
      case 'sent_request':
        return i18n('userProfile.statusRequestSent');
      case 'received_request':
        return i18n('userProfile.statusRequestReceived');
      case 'add_friend':
        return i18n('userProfile.statusAddFriend');
      default:
        assertNever(context);
    }
  }

  private async handleVisit() {
    if (!this.currentPlayerId) return;

    if (!this.currentData?.hasPublicProfile) {
      await openPopupPromise('popupModalWhite', {
        title: i18n('userProfile.visitError.notAvailable.title'),
        message: i18n('userProfile.visitError.notAvailable.message'),
        tracking: {
          feature: Feature.FRIENDS._,
          $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
        },
        additionalTracking: {
          feature: Feature.FRIENDS._,
          $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
        },
      });
      return;
    }

    const visitStarted = await visitFriend(this.currentPlayerId);
    if (visitStarted) {
      this.closeHandler('visit');
    }
  }

  private getRequest(playerId: string): FriendshipRequest | undefined {
    return user().friendshipRequests[playerId] as FriendshipRequest | undefined;
  }

  private async handleAccept() {
    if (!this.currentPlayerId) return;

    const request = this.getRequest(this.currentPlayerId);
    if (!request) return;

    const success = await acceptFriendRequest(request, 'hud');
    if (success) {
      this.closeHandler('acceptRequest');
    }
  }

  private async handleReject() {
    if (!this.currentPlayerId) return;

    const request = this.getRequest(this.currentPlayerId);
    if (!request) return;

    const success = await rejectFriendRequest(request, 'hud');
    if (success) {
      this.closeHandler('rejectRequest');
    }
  }
}
